import { Build } from "@stencil/core";
import { CookiePreferences } from "./Device.interface";
import { storageGet, storageSet } from "./storage";

const CONSENT_COOKIE_KEY = "sosolutions-cookie-consent";
const CONSENT_VERSION = "1";
const CONSENT_MAX_AGE_DAYS = 365;

const defaultPreferences: CookiePreferences = {
  status: "declined",
  analytics: false,
  marketing: false,
  updatedAt: "",
  version: CONSENT_VERSION,
};

function encodePreferences(preferences: CookiePreferences): string {
  return encodeURIComponent(JSON.stringify(preferences));
}

function decodePreferences(raw?: string): CookiePreferences | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as CookiePreferences;
    return upgradePreferences(parsed);
  } catch (e) {
    console.warn("COOKIE: Failed to parse preferences", e);
    return undefined;
  }
}

function upgradePreferences(
  preferences?: Partial<CookiePreferences>
): CookiePreferences {
  const now = new Date().toISOString();
  return {
    ...defaultPreferences,
    ...preferences,
    version: CONSENT_VERSION,
    updatedAt: preferences?.updatedAt || now,
  };
}

function readBrowserCookie(key: string): string | undefined {
  if (!Build.isBrowser) return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${key}=`));
  if (!match) return undefined;
  return match.split("=")[1];
}

function writeBrowserCookie(value: string) {
  if (!Build.isBrowser) return;
  const expires = new Date(
    Date.now() + CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
  ).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE_KEY}=${value}; Path=/; Expires=${expires}; SameSite=Lax${secure}`;
}

async function loadConsentPreferences(): Promise<CookiePreferences | undefined> {
  // Try browser cookie first for web, then fallback to stored preferences (native).
  const cookieValue = readBrowserCookie(CONSENT_COOKIE_KEY);
  const fromCookie = decodePreferences(cookieValue);
  if (fromCookie) return fromCookie;

  const stored = await storageGet(CONSENT_COOKIE_KEY);
  return stored ? upgradePreferences(stored as CookiePreferences) : undefined;
}

async function persistConsentPreferences(preferences: CookiePreferences) {
  const encoded = encodePreferences(preferences);
  writeBrowserCookie(encoded);
  await storageSet(CONSENT_COOKIE_KEY, preferences);
}

function buildConsentPreferences(
  partial?: Partial<CookiePreferences>
): CookiePreferences {
  return upgradePreferences(partial);
}

export {
  CONSENT_VERSION,
  buildConsentPreferences,
  loadConsentPreferences,
  persistConsentPreferences,
};
